import { Title, Meta } from "@solidjs/meta";
import { Component, JSX } from "solid-js";
import { env } from "~/config/env";

/**
 * SEO Component Props
 */
export interface SEOProps {
  /** Page title (will be suffixed with " - Absolut Radio") */
  title: string;
  /** Meta description for the page */
  description: string;
  /** Canonical URL for the page (e.g., "https://absolutradio.de/about") */
  url?: string;
  /** Open Graph image URL */
  image?: string;
  /** Image alt text for accessibility and LLM understanding */
  imageAlt?: string;
  /** Image width (recommended for OG) */
  imageWidth?: number;
  /** Image height (recommended for OG) */
  imageHeight?: number;
  /** Twitter card type (default: "summary_large_image") */
  twitterCard?: "summary" | "summary_large_image" | "app" | "player";
  /** Robots meta directive (default: "index, follow") */
  robots?: string;
  /** Page type for Open Graph (default: "website") */
  type?: "website" | "article" | "profile" | "music.podcast" | "music.episode" | (string & {});
  /** Article author name (for type="article") */
  author?: string;
  /** Article publish date (for type="article") */
  publishedTime?: string;
  /** Article modified date (for type="article") */
  modifiedTime?: string;
  /** Article section/category (for type="article") */
  section?: string;
  /** Article tags */
  tags?: string[];
  /** Locale (default: "de_DE") */
  locale?: string;
  /** Alternate locales */
  alternateLocales?: string[];
  /** Site name (default: "Absolut Radio") */
  siteName?: string;
  /** Keywords for LLM context (comma-separated) */
  keywords?: string;
  /** Append " - Absolut Radio" to the page title (default: true) */
  appendSiteName?: boolean;
  /** Structured data (JSON-LD) for rich snippets and LLMs */
  structuredData?: Record<string, any>;
  /** Additional meta tags */
  children?: JSX.Element;
}

/**
 * SEO Component
 *
 * Reusable component for managing page metadata optimized for 2025 SEO standards,
 * including LLM (Large Language Model) optimization through structured data.
 * Follows SolidStart best practices for server-side rendering.
 *
 * Key Features for 2025:
 * - Structured Data (JSON-LD) for LLM understanding
 * - Enhanced Open Graph tags with image dimensions
 * - Article metadata for content pages
 * - Multi-locale support
 * - Twitter Card optimization
 * - Canonical URLs for duplicate content handling
 *
 * @example Basic Usage
 * ```tsx
 * <SEO
 *   title="Über uns"
 *   description="Erfahre mehr über Absolut Radio"
 *   url="https://absolutradio.de/about"
 *   image="https://absolutradio.de/og-image.jpg"
 *   imageAlt="Absolut Radio Logo"
 * />
 * ```
 *
 * @example Article with Full SEO
 * ```tsx
 * <SEO
 *   title="Neue Single von Taylor Swift"
 *   description="Entdecke die neueste Single..."
 *   url="https://absolutradio.de/news/taylor-swift-neue-single"
 *   type="article"
 *   author="Max Mustermann"
 *   publishedTime="2025-11-06T10:00:00Z"
 *   section="Musik News"
 *   tags={["Taylor Swift", "Pop", "Neue Single"]}
 *   image="https://absolutradio.de/news/taylor.jpg"
 *   imageAlt="Taylor Swift neue Single Cover"
 *   imageWidth={1200}
 *   imageHeight={630}
 *   keywords="Taylor Swift, neue Single, Pop Musik, Absolut Radio"
 * />
 * ```
 *
 * @example Custom Structured Data for Events
 * ```tsx
 * <SEO
 *   title="Live Konzert Berlin"
 *   description="Rock-Konzert in Berlin"
 *   url="https://absolutradio.de/events/berlin-concert"
 *   structuredData={{
 *     "@context": "https://schema.org",
 *     "@type": "Event",
 *     name: "Rock Konzert Berlin",
 *     startDate: "2025-12-01T20:00:00+01:00",
 *     location: {
 *       "@type": "Place",
 *       name: "Mercedes-Benz Arena",
 *       address: "Berlin, Germany"
 *     }
 *   }}
 * />
 * ```
 */
/**
 * Site-wide fallback description. Empty meta/og/twitter description tags are
 * treated as missing by social crawlers, so always emit at least this default.
 */
const DEFAULT_DESCRIPTION =
  "Absolut Radio - Höre deine Lieblingsmusik live im Stream. Die besten Hits, aktuelle Charts und mehr. Jetzt kostenlos online hören!";

// Dimensions of the default /og-image.png (Open Graph standard 1.91:1)
const DEFAULT_IMAGE_WIDTH = 1200;
const DEFAULT_IMAGE_HEIGHT = 630;

export const SEO: Component<SEOProps> = (props) => {
  const fullTitle = () =>
    props.appendSiteName === false ? props.title : `${props.title} - Absolut Radio`;
  const description = () => props.description || DEFAULT_DESCRIPTION;
  const image = () => props.image || `${env.siteUrl}/og-image.png`;
  const imageWidth = () => (props.image ? props.imageWidth : DEFAULT_IMAGE_WIDTH);
  const imageHeight = () => (props.image ? props.imageHeight : DEFAULT_IMAGE_HEIGHT);
  const twitterCard = () => props.twitterCard || "summary_large_image";
  const robots = () => props.robots || "index, follow";
  const pageType = () => props.type || "website";
  const locale = () => props.locale || "de_DE";
  const siteName = () => props.siteName || "Absolut Radio";

  // Generate default structured data for LLMs
  const defaultStructuredData = () => ({
    "@context": "https://schema.org",
    "@type": pageType() === "article" ? "Article" : "WebPage",
    headline: props.title,
    description: description(),
    url: props.url,
    ...(image() && {
      image: {
        "@type": "ImageObject",
        url: image(),
        ...(props.imageAlt && { caption: props.imageAlt }),
        ...(imageWidth() && { width: imageWidth() }),
        ...(imageHeight() && { height: imageHeight() }),
      },
    }),
    ...(props.author && {
      author: {
        "@type": "Person",
        name: props.author,
      },
    }),
    ...(props.publishedTime && { datePublished: props.publishedTime }),
    ...(props.modifiedTime && { dateModified: props.modifiedTime }),
    ...(props.tags && { keywords: props.tags.join(", ") }),
  });

  return (
    <>
      {/* Page Title */}
      <Title>{fullTitle()}</Title>

      {/* Canonical URL - Critical for SEO 2025 */}
      {props.url && <link rel="canonical" href={props.url} />}

      {/* Standard Meta Tags */}
      <Meta name="description" content={description()} />
      <Meta name="robots" content={robots()} />
      <Meta name="keywords" content={props.keywords || "absolut, radio, streaming"} />
      <Meta name="author" content={props.author || "Absolut Radio"} />

      {/* Open Graph Tags */}
      <Meta property="og:site_name" content={siteName()} />
      <Meta property="og:title" content={fullTitle()} />
      <Meta property="og:description" content={description()} />
      <Meta property="og:type" content={pageType()} />
      <Meta property="og:locale" content={locale()} />
      {props.alternateLocales?.map((altLocale) => (
        <Meta property="og:locale:alternate" content={altLocale} />
      ))}
      {props.url && <Meta property="og:url" content={props.url} />}

      {/* Image Tags — falls back to global OG image */}
      <Meta property="og:image" content={image()} />
      {props.imageAlt && <Meta property="og:image:alt" content={props.imageAlt} />}
      {imageWidth() && <Meta property="og:image:width" content={imageWidth()!.toString()} />}
      {imageHeight() && <Meta property="og:image:height" content={imageHeight()!.toString()} />}

      {/* Article-specific OG tags */}
      {pageType() === "article" && (
        <>
          {props.author && <Meta property="article:author" content={props.author} />}
          {props.publishedTime && (
            <Meta property="article:published_time" content={props.publishedTime} />
          )}
          {props.modifiedTime && (
            <Meta property="article:modified_time" content={props.modifiedTime} />
          )}
          {props.section && <Meta property="article:section" content={props.section} />}
          {props.tags?.map((tag) => (
            <Meta property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Twitter Card Tags */}
      <Meta name="twitter:card" content={twitterCard()} />
      <Meta name="twitter:title" content={fullTitle()} />
      <Meta name="twitter:description" content={description()} />
      <Meta name="twitter:image" content={image()} />
      {props.imageAlt && <Meta name="twitter:image:alt" content={props.imageAlt} />}

      {/* Structured Data (JSON-LD) - Critical for LLM SEO 2025 */}
      <script type="application/ld+json">
        {JSON.stringify(props.structuredData || defaultStructuredData())}
      </script>

      {/* Additional meta tags from children */}
      {props.children}
    </>
  );
};
